import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Bell, Check, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";

import { type NudgeInboxItem, ackNudge, dismissNudge, markNotificationRead } from "@ando/core";
import { Popover, PopoverContent, PopoverTrigger, cn, toast } from "@ando/ui";

import { getWsClient } from "@/features/api";
import {
  NUDGE_INBOX_QUERY_KEY,
  NUDGE_UNREAD_QUERY_KEY,
  useNudgeInbox,
  useNudgeUnreadCount,
} from "../hooks/useNudgeInbox";
import {
  NOTIFICATIONS_INBOX_QUERY_KEY,
  NOTIFICATIONS_UNREAD_QUERY_KEY,
  useNotificationsInbox,
  useNotificationsUnreadCount,
} from "../hooks/useNotificationsInbox";
import { nudgeDeepLink } from "./nudgeDeepLink";
import { notificationDeepLink } from "./notificationDeepLink";

/**
 * One row shape both feeds (nudges + system notifications, see #922) get
 * normalized into so the popover renders a single, date-sorted list instead
 * of two disconnected sections. ``kind`` routes the row's actions (ack vs
 * mark-read) back to the right mutation/endpoint.
 */
interface InboxRow {
  readonly key: string;
  readonly kind: "nudge" | "notification";
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly urgency: "low" | "normal" | "high" | "urgent";
  readonly created_at: string;
  readonly link: {
    to: string;
    params?: Record<string, string>;
    search?: Readonly<Record<string, string>>;
  } | null;
}

/**
 * NotificationBell — top-right header bell with the unread-nudge count.
 *
 * Reads `GET /api/v1/nudges/unread-count` every 5 minutes (gentle poll,
 * matches v1 cadence). Real-time refresh comes via the `nudge_received`
 * WS frame — listener invalidates the query so the badge updates
 * instantly when an agent surfaces a new suggestion.
 *
 * ── May 24, 2026 — wired ``Popover`` inbox panel ─────────────────────
 *
 * The bell now opens a 320px popover listing the latest unread nudges
 * (``GET /nudges/inbox``). Each row is the title + body + urgency dot,
 * with two affordances:
 *   - ``Check``  → mark-as-read (``POST /nudges/{id}/ack``)
 *   - ``X``      → dismiss (``POST /nudges/{id}/dismiss``)
 * Both mutate the unread-count cache optimistically so the badge
 * collapses immediately.
 *
 * The panel is purely a quick-glance inbox — the full Wave-4 inbox
 * page (with filters, pagination, history) ships later. For now this
 * is the natural primary action.
 */

// Query keys + polling cadence live in ``useNudgeInbox`` so the bell
// AND the /workspace/work "Notifiche di Ando" section share the same
// cache (single invalidation point on ``nudge_received`` WS frames).

const URGENCY_DOT: Readonly<Record<NudgeInboxItem["urgency"], string>> = {
  urgent: "bg-error",
  high: "bg-ando-orange",
  normal: "bg-ink-3",
  low: "bg-ink-4",
};

export const NotificationBell: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const unreadQuery = useNudgeUnreadCount();
  const notificationsUnreadQuery = useNotificationsUnreadCount();
  // ``enabled: open`` keeps the cold-load cost off every page mount —
  // the popover hydrates the list the moment the user clicks the bell.
  const inboxQuery = useNudgeInbox({ limit: 8, enabled: open });
  const notificationsInboxQuery = useNotificationsInbox({ limit: 8, enabled: open });

  // WS push refresh — invalidate on every `nudge_received` frame so the
  // badge updates instantly without waiting for the 5-min poll.
  useEffect(() => {
    const ws = getWsClient();
    const off = ws.onMessage<{ type: "nudge_received" }>("nudge_received", () => {
      void queryClient.invalidateQueries({ queryKey: NUDGE_UNREAD_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: NUDGE_INBOX_QUERY_KEY });
    });
    return off;
  }, [queryClient]);

  const ackMutation = useMutation({
    mutationFn: (id: string) => ackNudge(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NUDGE_UNREAD_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: NUDGE_INBOX_QUERY_KEY });
    },
    onError: (err) => toast.error(t`Impossibile leggere: ${err.message}`),
  });

  const dismissMutation = useMutation({
    mutationFn: (id: string) => dismissNudge(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NUDGE_UNREAD_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: NUDGE_INBOX_QUERY_KEY });
    },
    onError: (err) => toast.error(t`Impossibile rimuovere: ${err.message}`),
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_INBOX_QUERY_KEY });
    },
    onError: (err) => toast.error(t`Impossibile leggere: ${err.message}`),
  });

  const count = (unreadQuery.data?.unread_count ?? 0) + (notificationsUnreadQuery.data?.unread_count ?? 0);
  const displayCount = count > 9 ? "9+" : String(count);
  const hasUnread = count > 0;

  const nudgeRows: InboxRow[] = (inboxQuery.data?.items ?? []).map((n) => ({
    key: `nudge:${n.id}`,
    kind: "nudge",
    id: n.id,
    title: n.title,
    body: n.body,
    urgency: n.urgency,
    created_at: n.created_at,
    link: nudgeDeepLink(n),
  }));
  const notificationRows: InboxRow[] = (notificationsInboxQuery.data ?? []).map((n) => ({
    key: `notification:${n.notification_id}`,
    kind: "notification",
    id: n.notification_id,
    title: n.title,
    body: n.body,
    urgency: n.priority === "high" ? "high" : n.priority === "low" ? "low" : "normal",
    created_at: n.created_at,
    link: notificationDeepLink(n),
  }));
  const isLoading = inboxQuery.isLoading || notificationsInboxQuery.isLoading;
  const items = [...nudgeRows, ...notificationRows].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={hasUnread ? t`${count} notifiche da Ando in attesa` : t`Notifiche da Ando`}
          className={cn(
            // Match StudioPill chrome so the trio (Bell / Studio / Avatar)
            // reads as one block.
            "relative inline-flex h-8 w-8 items-center justify-center rounded-full",
            "border border-white/60 bg-white/70 backdrop-blur-md backdrop-saturate-160",
            "shadow-xs transition-all hover:bg-white/90 active:scale-[0.97]",
            "text-ink-2 hover:text-ink-1",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1",
            className,
          )}
        >
          <Bell size={14} strokeWidth={1.75} aria-hidden />
          {hasUnread ? (
            <span
              aria-hidden
              className={cn(
                "-top-0.5 -right-0.5 absolute inline-flex min-w-4 items-center justify-center rounded-full px-1",
                "h-3.5 bg-ando-orange font-semibold text-[9px] text-white tabular-nums leading-[14px]",
                "ring-2 ring-cream",
              )}
            >
              {displayCount}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="start" sideOffset={10} className="w-[320px] p-0">
        <div className="flex items-center justify-between border-hairline border-b px-3 py-2">
          <span className="font-medium text-[12px] text-ink-1">
            <Trans>Notifiche</Trans>
          </span>
          {hasUnread ? (
            <span className="font-medium text-[10px] text-ando-orange uppercase tracking-[0.05em]">
              <Trans>{count} non lette</Trans>
            </span>
          ) : null}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 px-4 py-8 text-[12px] text-ink-3">
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.75} />
              <Trans>Caricamento…</Trans>
            </div>
          ) : items.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <p className="font-medium text-[13px] text-ink-1">
                <Trans>Sei in pari ✨</Trans>
              </p>
              <p className="mt-1 text-[11px] text-ink-3">
                <Trans>Ando ti avviserà qui quando avrà qualcosa di nuovo per te.</Trans>
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-hairline">
              {items.map((row) => (
                <InboxRowView
                  key={row.key}
                  row={row}
                  onAck={() =>
                    row.kind === "nudge"
                      ? ackMutation.mutate(row.id)
                      : markReadMutation.mutate(row.id)
                  }
                  onDismiss={row.kind === "nudge" ? () => dismissMutation.mutate(row.id) : null}
                  onNavigate={() => setOpen(false)}
                  busy={ackMutation.isPending || dismissMutation.isPending || markReadMutation.isPending}
                />
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

NotificationBell.displayName = "NotificationBell";

interface InboxRowViewProps {
  readonly row: InboxRow;
  readonly onAck: () => void;
  /** ``null`` hides the dismiss action (system notifications have none). */
  readonly onDismiss: (() => void) | null;
  /** Close the popover when the row deep-links to an in-app surface. */
  readonly onNavigate: () => void;
  readonly busy: boolean;
}

const InboxRowView: React.FC<InboxRowViewProps> = ({ row, onAck, onDismiss, onNavigate, busy }) => {
  const { i18n } = useLingui();
  const removeLabel = i18n._(msg`Rimuovi`);
  const ackLabel = i18n._(msg`Segna come letta`);
  return (
    <li className="group flex items-start gap-2 px-3 py-2.5 transition-colors hover:bg-black/[0.025]">
      <span
        aria-hidden
        className={cn(
          "mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full",
          URGENCY_DOT[row.urgency],
        )}
      />
      <div className="min-w-0 flex-1">
        {row.link ? (
          <Link
            to={row.link.to}
            params={row.link.params}
            search={row.link.search}
            onClick={onNavigate}
            className="block truncate font-medium text-[12.5px] text-ink-1 hover:text-ando-orange focus-visible:text-ando-orange focus-visible:outline-none"
          >
            {row.title}
          </Link>
        ) : (
          <p className="truncate font-medium text-[12.5px] text-ink-1">{row.title}</p>
        )}
        <p className="mt-0.5 line-clamp-2 text-[11.5px] text-ink-2 leading-snug">{row.body}</p>
      </div>
      <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
        <button
          type="button"
          onClick={onAck}
          disabled={busy}
          aria-label={ackLabel}
          title={ackLabel}
          className="grid h-6 w-6 place-items-center rounded text-ink-3 transition-colors hover:bg-white hover:text-ando-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange disabled:opacity-40"
        >
          <Check className="h-3 w-3" strokeWidth={2} aria-hidden />
        </button>
        {onDismiss ? (
          <button
            type="button"
            onClick={onDismiss}
            disabled={busy}
            aria-label={removeLabel}
            title={removeLabel}
            className="grid h-6 w-6 place-items-center rounded text-ink-3 transition-colors hover:bg-white hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange disabled:opacity-40"
          >
            <X className="h-3 w-3" strokeWidth={2} aria-hidden />
          </button>
        ) : null}
      </div>
    </li>
  );
};
InboxRowView.displayName = "InboxRowView";

/**
 * Back-compat alias — the AuthedShell still imports `NotificationPill` by
 * name. Re-export as alias to avoid a coordinated rename across multiple
 * files. New callers should import `NotificationBell` directly.
 */
export const NotificationPill = NotificationBell;
